import { types, Instance } from "mobx-state-tree";
import {
  fetchAuthSession,
  fetchUserAttributes,
  fetchMFAPreference,
  FetchMFAPreferenceOutput,
  getCurrentUser,
} from "aws-amplify/auth";
import { generateClient } from "aws-amplify/api";
import { customGetUser } from "../graphql/customQueries";
import { AccountTypeName, CustomGetUserQuery } from "../API";
import {
  AccountProps,
  AccountSettingsProps,
  FileUploadProps,
} from "./platformSchema";
import { UserAccountMFA } from "../types/enum/UserAccountMFA";
import { AuthSession } from "../types/awsTypes";
import { getAccessTokenForLambdaAuth } from "../utils/authHelpers";

// Graphql Client
const client = generateClient();

const accountDataModel = types.model({
  // id: types.string,
  // name: types.string,
  ...AccountProps,
  // phone: types.maybeNull(types.string)
});
export type IAccountDataModel = Instance<typeof accountDataModel>;

const accountSettingsModel = types.model({
  ...AccountSettingsProps,
});
export type IAccountSettingsModel = Instance<typeof accountSettingsModel>;

const userAttributesModel = types.model({
  sub: types.string,
  identityId: types.string,
  email: types.string,
  phone_number: types.maybeNull(types.string),
  "custom:mfaConfig": types.maybeNull(types.string),
});
export type IUserAttributesModel = Instance<typeof userAttributesModel>;

const fileUploadModel = types.model({
  ...FileUploadProps,
  // originalName: types.string,
  createdAt: types.string,
  fileName: types.string,
  // fileStatus: types.string,
  // fileError: types.maybeNull(types.string),
  // numRecords: types.maybeNull(types.number),
  // numErrors: types.maybeNull(types.number),
});
export type IFileUploadModel = Instance<typeof fileUploadModel>;
export type IFileUploadsModel = Instance<typeof fileUploadModel>[];
const fontModel = types.model({
  fontFamily: types.string,
  variants: types.array(
    types.model({
      name: types.string,
      weight: types.number,
      style: types.string,
      url: types.maybeNull(types.string),
    })
  ),
});
export type IFontModel = Instance<typeof fontModel>;
export type IFontsModel = Instance<typeof fontModel>[];

// Model for a single account's fonts
const accountFontsModel = types.model({
  id: types.identifier, // The 'id' field will serve as a unique identifier for each account
  fonts: types.array(fontModel),
});

const accountUserModel = types.model({
  id: types.string,
  accountId: types.string,
  userId: types.string,
  permissions: types.optional(types.array(types.string), []),
  account: types.model({
    id: types.identifier,
    gid: types.string,
    name: types.string,
    mfaEnabled: types.maybeNull(types.boolean),
    tierId: types.maybeNull(types.string),
    createdAt: types.string,
    updatedAt: types.string,
  }),
});

export const accountModel = types
  .model("accountStore", {
    accounts: types.optional(types.array(accountUserModel), []),
    accountData: types.maybe(accountDataModel),
    mfaEnabled: types.maybe(types.boolean),
    /**
     * This is the MFA configuration from the organization account settings (The account on dynamo DB)
     */
    organizationRequireMFA: types.maybeNull(types.boolean),
    /**
     * This is the MFA configuration from the user account settings (The account on cognito)
     * 0 - Unset - Follow the organization settings
     * 1 - Disabled - MFA is not required
     * 2 - Enabled - MFA is required
     */
    personalAccountMFARequired: types.maybeNull(types.number),
    mfaPreferences: types.maybeNull(types.string),
    mfaEnabledArray: types.optional(types.array(types.string), []),
    userAttributes: types.maybe(userAttributesModel),
    fileUploads: types.optional(types.array(fileUploadModel), []),
    hubspotConnection: types.maybeNull(types.string),
    // fonts: types.optional(types.array(fontModel), []),
    fontsPerAccountCollectionModel: types.optional(
      types.map(accountFontsModel),
      {}
    ),
    loadStatus: types.optional(
      types.enumeration("LoadStatus", ["unloaded", "loading", "loaded"]),
      "unloaded"
    ),
  })
  .volatile((self) => ({
    loadCallbackFunctions: [] as (() => void)[],
  }))
  .views((self) => ({
    get isAdmin() {
      return self.accounts.some((au) => au.accountId === "blingsadmin");
    },
    get isBlingsAccount() {
      const hasBlingsAccount = self.accounts.some(
        (au) => au.accountId === "blings_account"
      );
      return (
        hasBlingsAccount || self.userAttributes?.email.endsWith("@blings.io")
      );
    },
    get hubspotCredentialsAreValid() {
      if (!self.hubspotConnection) return false;
      const cred = JSON.parse(self.hubspotConnection);
      if (Date.now() >= cred.updatedAt + cred.expiresIn * 1000) {
        return false;
      }
      return true;
    },
    get allAccountIds() {
      return self.accounts.map((au) => au.accountId);
    },
    getFontsForAccount(accountId?: string | null) {
      if (!accountId) return undefined;
      return self.fontsPerAccountCollectionModel.get(accountId)?.fonts;
    },

    /**
     * Get the MFA configuration for the user. Personal MFA configuration takes precedence over organization MFA configuration
     */
    get MFARequired() {
      if (self.personalAccountMFARequired === UserAccountMFA.UNSET) {
        return self.organizationRequireMFA;
      }
      return self.personalAccountMFARequired === UserAccountMFA.ENABLED;
    },
    get MFAEnabled() {
      if (self.mfaEnabledArray.length !== 0) {
        return true;
      }
      return false;
    },
    get UserMFAPreference() {
      return self.mfaPreferences;
    },
  }))
  .actions((self) => ({
    addFunctionToLoadCallback(func: () => void) {
      if (self.loadStatus !== "loaded") self.loadCallbackFunctions.push(func);
      else func();
    },
    removeFunctionFromLoadCallback(func: () => void) {
      const index = self.loadCallbackFunctions.indexOf(func);
      if (index > -1) {
        self.loadCallbackFunctions.splice(index, 1);
      }
    },
    addFontsPerAccount(accountId: string, fonts: IFontsModel) {
      self.fontsPerAccountCollectionModel.put({
        id: accountId,
        fonts,
      });
    },
    setAccounts(
      accounts: Array<{
        id: string;
        accountId: string;
        userId: string;
        permissions: string[];
        account: {
          id: string;
          gid: string;
          name: string;
          mfaEnabled: boolean | null;
          tierId: string | null;
          createdAt: string;
          updatedAt: string;
        };
      }>
    ) {
      self.accounts.replace(accounts as any);
    },
    setUserAttributes(attr: IUserAttributesModel) {
      // const {email} = attr
      self.userAttributes = attr;
    },
    setAccountData(accountData: IAccountDataModel) {
      self.accountData = accountData;
    },
    setMfaEnabled(mfaEnabled: boolean) {
      self.mfaEnabled = mfaEnabled;
    },
    setFileUploads(fileUploads: any) {
      self.fileUploads.replace(fileUploads);
    },
    setOrganizationRequireMFA(enforcedMFA: boolean) {
      self.organizationRequireMFA = enforcedMFA;
    },
    setPersonalAccountMFARequired(mfaConfig: number) {
      self.personalAccountMFARequired = mfaConfig;
    },
    setMFAPreferences(mfaPreferences: string) {
      self.mfaPreferences = mfaPreferences;
    },
    setMFAEnabledArray(mfaEnabledArray: string[]) {
      self.mfaEnabledArray.replace(mfaEnabledArray);
    },

    // setFonts(fonts: IFontsModel) {
    //   self.fonts.replace(fonts);
    // },
    setFontsPerAccount(data: { [key: string]: IFontsModel }) {
      self.fontsPerAccountCollectionModel.clear();
      Object.keys(data).forEach((key) => {
        self.fontsPerAccountCollectionModel.put({
          id: key,
          fonts: data[key],
        });
      });
    },
    setLoadStatus(status: "loading" | "loaded") {
      self.loadStatus = status;
    },
  }))
  .actions((self) => ({
    async loadAccountData() {
      self.setLoadStatus("loading");
      try {
        // Step 1: Get Amplify user ID
        const amplifyUser = await getCurrentUser();

        // Step 2: Get user with all accounts using customGetUser (with pagination)
        const promises = [
          fetchAuthSession(),
          fetchUserAttributes(),
          fetchMFAPreference(),
        ];
        const authResult = await Promise.all(promises);
        const fullAuth = authResult[0] as AuthSession;
        const attributes = authResult[1] as IUserAttributesModel;
        const {
          preferred,
          enabled,
        } = authResult[2] as FetchMFAPreferenceOutput;

        if (!attributes.email) throw new Error("No email");

        const attr: IUserAttributesModel = {
          sub: attributes.sub as string,
          email: attributes.email,
          phone_number: attributes.phone_number || null,
          identityId: fullAuth.identityId as string,
          "custom:mfaConfig": attributes["custom:mfaConfig"] || null,
        };
        self.setUserAttributes(attr);
        self.setPersonalAccountMFARequired(
          attr["custom:mfaConfig"]
            ? parseInt(attr["custom:mfaConfig"])
            : UserAccountMFA.UNSET
        );
        self.setMFAEnabledArray(enabled || []);
        if (preferred) {
          self.setMFAPreferences(preferred);
        }

        // Step 3: Fetch user and accounts from DynamoDB (with pagination)
        const allAccountUsers: Array<{
          id: string;
          accountId: string;
          account: {
            id: string;
            gid: string;
            name: string;
            mfaEnabled?: boolean | null;
            createdAt: string;
            updatedAt: string;
            accountType?: {
              levelName: any;
            } | null;
            fonts?: Array<{
              fontFamily: string;
              variants: Array<{
                name: string;
                weight: number;
                style: string;
                url?: string | null;
              }>;
            } | null> | null;
          } | null;
        }> = [];
        let nextToken: string | null = null;
        let dbUser: CustomGetUserQuery["getUser"] = null;

        do {
          const authToken = await getAccessTokenForLambdaAuth();

          const response: { data: CustomGetUserQuery } = await client.graphql({
            query: customGetUser,
            variables: {
              id: amplifyUser.userId,
              accountsLimit: 100, // Fetch up to 100 accounts per request
              ...(nextToken && { accountsNextToken: nextToken }),
            },
            authMode: "lambda",
            authToken,
          });

          const userData = response.data;

          if (!dbUser) {
            dbUser = userData?.getUser || null;
            if (!dbUser) {
              throw new Error("User not found in database");
            }
          }

          const accountUsers = userData?.getUser?.accounts?.items || [];
          accountUsers.forEach((item) => {
            if (
              item &&
              item.account &&
              item.account.tierId === AccountTypeName.ENTERPRISE
            ) {
              allAccountUsers.push({
                id: item.id,
                accountId: item.accountId,
                account: item.account,
              });
            }
          });

          nextToken = userData?.getUser?.accounts?.nextToken || null;
        } while (nextToken);

        // Step 4: Set all accounts
        if (allAccountUsers.length === 0) {
          throw new Error("No accounts found for user");
        }

        if (allAccountUsers.length > 1) {
          console.log("User has multiple accounts:", allAccountUsers.length);
        }

        // Set all accounts in the store
        self.setAccounts(
          allAccountUsers.map((au) => ({
            id: au.id,
            accountId: au.accountId,
            userId: dbUser!.id,
            permissions: [],
            account: {
              id: au.account!.id,
              gid: au.account!.gid,
              name: au.account!.name,
              mfaEnabled: au.account!.mfaEnabled ?? null,
              tierId: null,
              createdAt: au.account!.createdAt,
              updatedAt: au.account!.updatedAt,
            },
          }))
        );

        // Account-level fonts, shown as their own category in the font picker.
        // A font row that does not match the model must not take the whole
        // account load (and with it MFA setup) down with it.
        try {
          self.setFontsPerAccount(
            allAccountUsers.reduce((acc, au) => {
              const fonts = (au.account?.fonts || []).filter(Boolean);
              if (fonts.length) {
                acc[au.accountId] = fonts.map((font) => ({
                  fontFamily: font!.fontFamily,
                  variants: (font!.variants || []).map((variant) => ({
                    name: variant.name,
                    weight: variant.weight,
                    style: variant.style,
                    url: variant.url ?? null,
                  })),
                })) as IFontsModel;
              }
              return acc;
            }, {} as { [accountId: string]: IFontsModel })
          );
        } catch (e) {
          console.error("Failed to load account fonts", e);
        }

        // Step 5: Load account data from the first account
        const firstAccount = allAccountUsers[0].account;
        if (firstAccount) {
          self.setAccountData({
            id: firstAccount.id,
            name: firstAccount.name,
            createdAt: firstAccount.createdAt,
            updatedAt: firstAccount.updatedAt,
          });

          // Set MFA from the first account
          if (firstAccount.mfaEnabled) {
            self.setMfaEnabled(true);
            self.setOrganizationRequireMFA(true);
          }
        }

        // Check MFA settings across all accounts
        allAccountUsers.forEach((accountUser) => {
          if (accountUser.account?.mfaEnabled) {
            self.setMfaEnabled(true);
            self.setOrganizationRequireMFA(true);
          }
        });
      } catch (e) {
        console.log("Error loading account data:", e);
        console.error(e);
      }
      self.setLoadStatus("loaded");
      self.loadCallbackFunctions.forEach((func) => func());
    },
  }));
export default accountModel.create({});
export type IaccountModel = Instance<typeof accountModel>;
